import React from 'react';
import SEO from '../components/SEO';
import { TrustChip, TRUST_STATES, TRUST_VARIANTS, TRUST_SIZES, FOUNDER_TIERS } from '../components/TrustChip';

/**
 * D-067 · TrustChip showcase (review-only · noindex).
 * Internal preview surface so the founder can ratify the primitive
 * before any production wiring begins.
 */

const CARD_FACTORIES = [
  { name: 'Hoa Sen Garments', country: 'Vietnam',  state: 'elite-verified', sub: 'Knitwear · 1,200 staff' },
  { name: 'Tirupur Atelier',   country: 'India',    state: 'trusted',        sub: 'Cotton jersey · 460 staff' },
  { name: 'Porto Têxtil',      country: 'Portugal', state: 'verified',       sub: 'Woven · 220 staff' },
  { name: 'Shenzhen Linewear', country: 'China',    state: 'under-review',   sub: 'Outerwear · 700 staff' },
];

const FOUNDERS_WALL = [
  { number: 1, tier: 'gold',       name: 'Aroha Williams',     company: 'Example Brand',     tierLabel: 'Gold' },
  { number: 1,  tier: 'elite',      name: 'Marco Bianchi',      company: 'Bianchi Retail',    tierLabel: 'Elite' },
  { number: 1,  tier: 'enterprise', name: 'Sarah Chen',         company: 'Chen Sourcing',     tierLabel: 'Enterprise' },
];

const Section = ({ overline, title, children }) => (
  <section className="border-t border-neutral-200 py-12 md:py-16">
    <span className="label-overline text-neutral-500">{overline}</span>
    <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-4xl" style={{ fontWeight: 700 }}>{title}</h2>
    <div className="mt-8">{children}</div>
  </section>
);

const SwatchTable = ({ size, variant }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] border-collapse text-sm">
      <thead>
        <tr className="border-b border-neutral-300">
          <th className="py-2 text-left label-overline text-neutral-500 w-[28%]">State</th>
          <th className="py-2 text-left label-overline text-neutral-500">{variant} · {size}</th>
          <th className="py-2 text-left label-overline text-neutral-500">+ dot</th>
          <th className="py-2 text-left label-overline text-neutral-500">+ founder #</th>
        </tr>
      </thead>
      <tbody>
        {TRUST_STATES.map((state) => (
          <tr key={state} className="border-b border-neutral-100 align-middle">
            <td className="py-3 pr-4 font-mono-data text-[11px] text-neutral-700">{state}</td>
            <td className="py-3 pr-4"><TrustChip state={state} variant={variant} size={size} testId={`chip-${state}-${variant}-${size}`} /></td>
            <td className="py-3 pr-4"><TrustChip state={state} variant={variant} size={size} withDot /></td>
            <td className="py-3 pr-4">
              {state === 'founding-partner'
                ? <TrustChip state={state} variant={variant} size={size} founderTier="gold" founderNumber={1} internal />
                : <span className="font-mono-data text-[11px] text-neutral-400">n/a</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TrustChipPreview = () => (
  <div data-testid="trust-chip-preview-page" className="bg-white">
    <SEO title="TrustChip preview — GIWAHS (review-only)" description="Internal review surface for the TrustChip primitive." noindex />

    <section className="border-b border-black">
      <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-24">
        <span className="label-overline text-neutral-500">/ D-067 · Review draft</span>
        <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
          TrustChip — universal trust primitive.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-700">
          One component, five states, three variants, three sizes. Inherits the D-060 brand lock palette
          and is the single source of truth for every &ldquo;verified&rdquo; mark anywhere on the platform.
          This page is <code className="bg-neutral-100 px-1">noindex</code> and is not wired into any
          production surface — review only.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <TrustChip state="verified"          />
          <TrustChip state="trusted"           />
          <TrustChip state="elite-verified"    />
          <TrustChip state="founding-partner"  founderTier="gold"       founderNumber={1} internal />
          <TrustChip state="founding-partner"  founderTier="elite"      founderNumber={1} internal />
          <TrustChip state="founding-partner"  founderTier="enterprise" founderNumber={1} internal />
          <TrustChip state="under-review"      />
        </div>
      </div>
    </section>

    <div className="mx-auto max-w-[1280px] px-5 md:px-8">

      {/* SOLID · SM */}
      <Section overline="/ 01 · Solid variant · sm (default)" title="The default chip.">
        <SwatchTable size="sm" variant="solid" />
      </Section>

      {/* OUTLINE · SM */}
      <Section overline="/ 02 · Outline variant · sm" title="On editorial light surfaces.">
        <div className="bg-[#F5F5F0] p-8 border border-neutral-300">
          <SwatchTable size="sm" variant="outline" />
        </div>
      </Section>

      {/* INVERSE · SM */}
      <Section overline="/ 03 · Inverse variant · sm" title="On dark heroes &amp; founder badges.">
        <div className="bg-[#0A0A0A] p-8">
          <table className="w-full min-w-[640px] border-collapse text-sm text-white">
            <thead>
              <tr className="border-b border-neutral-700">
                <th className="py-2 text-left label-overline text-neutral-400 w-[28%]">State</th>
                <th className="py-2 text-left label-overline text-neutral-400">inverse · sm</th>
                <th className="py-2 text-left label-overline text-neutral-400">+ dot</th>
                <th className="py-2 text-left label-overline text-neutral-400">+ founder #</th>
              </tr>
            </thead>
            <tbody>
              {TRUST_STATES.map((state) => (
                <tr key={state} className="border-b border-neutral-800 align-middle">
                  <td className="py-3 pr-4 font-mono-data text-[11px] text-neutral-300">{state}</td>
                  <td className="py-3 pr-4"><TrustChip state={state} variant="inverse" /></td>
                  <td className="py-3 pr-4"><TrustChip state={state} variant="inverse" withDot /></td>
                  <td className="py-3 pr-4">
                    {state === 'founding-partner'
                      ? <TrustChip state={state} variant="inverse" founderTier="elite" founderNumber={1} internal />
                      : <span className="font-mono-data text-[11px] text-neutral-500">n/a</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* SIZE SCALE */}
      <Section overline="/ 04 · Size scale" title="xs · sm · md.">
        <div className="space-y-6">
          {TRUST_SIZES.map((size) => (
            <div key={size} className="flex flex-wrap items-center gap-3 border border-neutral-200 p-5">
              <span className="label-overline text-neutral-500 mr-2 w-12">{size}</span>
              {TRUST_STATES.map((s) => (
                <TrustChip
                  key={s}
                  state={s}
                  size={size}
                  founderTier={s === 'founding-partner' ? 'gold' : null}
                  founderNumber={s === 'founding-partner' ? 14 : null}
                  internal
                />
              ))}
              {/* All three Founder tiers at this size */}
              {FOUNDER_TIERS.map((t, i) => (
                <TrustChip key={t} state="founding-partner" size={size} founderTier={t} founderNumber={1} internal />
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* PLACEMENT — FACTORY CARDS */}
      <Section overline="/ 05 · Placement · factory cards" title="In product surfaces.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CARD_FACTORIES.map((f) => (
            <div key={f.name} className="border border-black p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-base" style={{ fontWeight: 700 }}>{f.name}</div>
                  <div className="mt-0.5 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{f.country}</div>
                </div>
                <TrustChip state={f.state} size="xs" />
              </div>
              <div className="text-xs text-neutral-600">{f.sub}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* PLACEMENT — FOUNDERS WALL */}
      <Section overline="/ 06 · Placement · Founders Wall preview" title="Founder identity, live.">
        <div className="bg-[#0A0A0A] p-8">
          <table className="w-full text-white">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="py-3 text-left label-overline text-neutral-400 w-16">#</th>
                <th className="py-3 text-left label-overline text-neutral-400">Founder</th>
                <th className="py-3 text-left label-overline text-neutral-400">Company</th>
                <th className="py-3 text-left label-overline text-neutral-400">Mark</th>
              </tr>
            </thead>
            <tbody>
              {FOUNDERS_WALL.map((f) => (
                <tr key={f.number} className="border-b border-neutral-900">
                  <td className="py-4 font-mono-data text-[11px] text-neutral-400">#{String(f.number).padStart(3, '0')}</td>
                  <td className="py-4 text-sm">{f.name}</td>
                  <td className="py-4 text-sm text-neutral-400">{f.company}</td>
                  <td className="py-4"><TrustChip state="founding-partner" variant="inverse" founderNumber={f.number} internal /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* PLACEMENT — RFQ RESPONSE */}
      <Section overline="/ 07 · Placement · RFQ response" title="In line of evidence.">
        <div className="border border-black p-6 max-w-2xl">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg" style={{ fontWeight: 700 }}>Tirupur Atelier · India</div>
              <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">Response · RFQ-2026-021</div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <TrustChip state="trusted" size="xs" />
              <TrustChip state="verified" size="xs" withDot />
            </div>
          </div>
          <p className="mt-4 text-sm text-neutral-700">Lead time 28 days · MOQ 500 units · 100% cotton single jersey · GOTS + OEKO-TEX.</p>
        </div>
      </Section>

      {/* API */}
      <Section overline="/ 08 · API at a glance" title="Read once, ship everywhere.">
        <div className="bg-[#0A0A0A] text-white p-6 font-mono-data text-xs leading-relaxed overflow-x-auto whitespace-pre">{`import { TrustChip } from '@/components/TrustChip';

<TrustChip state="verified" />
<TrustChip state="trusted" size="md" />
<TrustChip state="elite-verified" variant="outline" />
<TrustChip state="founding-partner" founderTier="gold" founderNumber={1} />
<TrustChip state="founding-partner" founderTier="elite" founderNumber={1} variant="inverse" />
<TrustChip state="founding-partner" founderTier="enterprise" founderNumber={1} />
<TrustChip state="under-review" withDot href="/trust/security" />

// states:   ${TRUST_STATES.join(' · ')}
// variants: ${TRUST_VARIANTS.join(' · ')}
// sizes:    ${TRUST_SIZES.join(' · ')}
// tiers:    ${FOUNDER_TIERS.join(' · ')}`}</div>
      </Section>

      <div className="border-t border-neutral-200 py-10 font-mono-data text-xs text-neutral-500">
        Source: <code>/app/frontend/src/components/TrustChip.jsx</code> · Spec: <code>/app/memory/founder_vault/46_TRUSTCHIP_SPECIFICATION.md</code> · Status: review draft · not wired to production surfaces.
      </div>
    </div>
  </div>
);

export default TrustChipPreview;
